import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Plus, X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TaskReferencePhoto } from "@/hooks/useTaskReferencePhotos";
import { cn } from "@/lib/utils";

interface TaskReferencePhotoGalleryProps {
  photos: TaskReferencePhoto[];
  roomName: string;
  taskText: string;
  isUploading: boolean;
  onUpload: (files: File[]) => void;
  onDelete: (photo: TaskReferencePhoto) => void;
  disabled?: boolean;
}

const TaskReferencePhotoGallery = ({
  photos,
  roomName,
  taskText,
  isUploading,
  onUpload,
  onDelete,
  disabled = false,
}: TaskReferencePhotoGalleryProps) => {
  const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onUpload(Array.from(files));
      e.target.value = "";
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        onUpload(imageFiles);
      }
    }
  }, [disabled, onUpload]);

  const hasPhotos = photos.length > 0;
  const canAddMore = !disabled && roomName.trim() && taskText.trim();

  return (
    <div className="space-y-2">
      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileSelect}
      />

      {/* Photo Gallery / Drop Zone */}
      <div
        className={cn(
          "rounded-lg border-2 border-dashed p-3 sm:p-4 transition-colors",
          isDragOver && canAddMore ? "border-primary bg-primary/5" : "border-muted",
          !canAddMore && "opacity-50"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {hasPhotos ? (
          <div className="space-y-3">
            {/* Thumbnail Strip - horizontal scroll on mobile, wrap on larger screens */}
            <div className="flex gap-2 sm:gap-3 overflow-x-auto sm:overflow-x-visible sm:flex-wrap pb-2 sm:pb-0 -mx-1 px-1 scrollbar-thin snap-x snap-mandatory">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group shrink-0 snap-start"
                >
                  <button
                    type="button"
                    className={cn(
                      "rounded-lg overflow-hidden border bg-muted",
                      "w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20",
                      "active:scale-95 transition-transform"
                    )}
                    onClick={() => setViewingPhoto(photo.photo_url)}
                  >
                    <img
                      src={photo.photo_url}
                      alt="Reference"
                      className="w-full h-full object-cover cursor-pointer"
                      loading="lazy"
                    />
                  </button>
                  {/* View overlay */}
                  <div 
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer rounded-lg pointer-events-none"
                  >
                    <ZoomIn className="w-4 h-4 text-white" />
                  </div>
                  {/* Delete button - larger touch target */}
                  {!disabled && (
                    <button
                      type="button"
                      className="absolute -top-1.5 -right-1.5 w-6 h-6 sm:w-5 sm:h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity shadow-md active:scale-90"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(photo);
                      }}
                    >
                      <X className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Add More Button - inline with photos */}
              {canAddMore && (
                <button
                  type="button"
                  className={cn(
                    "shrink-0 snap-start rounded-lg border-2 border-dashed border-muted-foreground/30",
                    "flex items-center justify-center hover:border-primary hover:bg-primary/5 transition-colors",
                    "w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20",
                    "active:scale-95 active:bg-primary/10"
                  )}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  ) : (
                    <Plus className="w-6 h-6 sm:w-5 sm:h-5 text-muted-foreground" />
                  )}
                </button>
              )}
            </div>
            
            {/* Upload hint */}
            <p className="text-xs text-muted-foreground">
              {photos.length} photo{photos.length !== 1 ? 's' : ''} • Drag & drop or tap + to add more
            </p>
          </div>
        ) : (
          /* Empty State - full width button on mobile */
          <div className="py-3 sm:py-4 text-center">
            {isUploading ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  {canAddMore 
                    ? "Drag & drop images or tap to upload"
                    : "Enter room name and task text first"
                  }
                </p>
                {canAddMore && (
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    className="w-full sm:w-auto h-10 sm:h-8 text-sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Reference Photos
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskReferencePhotoGallery;
